import type { DerivedStats, MachineDef, MachineId, MasterDie, Rarity } from './types';
import {
  AFFIX_COUNT,
  MACHINE_BY_ID,
  RARITY_ORDER,
  RECYCLE_PERCENT,
  machineForSides,
} from '../data/machines';
import { BALANCE, type GachaLot } from '../data/balance';
import { rollAffixes } from './affixes';
import { createMasterDie, createNamedPrismatic, dieScore } from './dice';
import { gachaUnitCost } from './economy';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';
import type { Rng } from './rng';
import type { NodeLevels } from './skilltree';
import { nodeLevel } from './skilltree';
import { availableMaterials } from '../data/materials';

/** Lots achetables — MVP_DESIGN §3.4. */
export const GACHA_LOTS = BALANCE.gachaLots;

export function isValidLot(qty: number): qty is GachaLot {
  return (GACHA_LOTS as readonly number[]).includes(qty);
}

/**
 * Poids de **base** d'une machine après le glissement de rareté `shift`
 * (surcharge `rarityShift`, phase 6) : chaque rareté au-dessus de Commun cède
 * la part `shift` de son poids à la rareté **juste en dessous**. La somme des
 * poids est conservée. `shift` = 0 rend les poids de la machine tels quels.
 */
export function shiftedWeights(machine: MachineDef, shift: number): Record<Rarity, number> {
  const out = {} as Record<Rarity, number>;
  for (const rarity of RARITY_ORDER) out[rarity] = machine.weights[rarity] ?? 0;
  if (!(shift > 0)) return out;
  const s = Math.min(1, shift);
  for (let i = 1; i < RARITY_ORDER.length; i++) {
    const moved = (machine.weights[RARITY_ORDER[i]] ?? 0) * s;
    out[RARITY_ORDER[i]] -= moved;
    out[RARITY_ORDER[i - 1]] += moved;
  }
  return out;
}

/**
 * Poids de rareté effectifs d'une machine.
 * Les nœuds *Modification mécanique* et *Bidouilleur* multiplient les raretés
 * > Commun par (1 + 0,10 × niveaux cumulés) — `DerivedStats.rarityWeightMult`.
 * `rarityShift` (surcharge d'équilibrage, 0 par défaut) fait d'abord glisser
 * les poids de base vers les raretés basses (`shiftedWeights`).
 */
export function effectiveWeights(
  machine: MachineDef,
  rarityMult: number,
  rarityShift = 0,
): Record<Rarity, number> {
  const out = {} as Record<Rarity, number>;
  const shifted = rarityShift > 0 ? shiftedWeights(machine, rarityShift) : null;
  for (const rarity of RARITY_ORDER) {
    const base = shifted ? shifted[rarity] : (machine.weights[rarity] ?? 0);
    out[rarity] = rarity === 'common' ? base : base * rarityMult;
  }
  return out;
}

/** Tire une rareté selon les poids effectifs. */
export function rollRarity(machine: MachineDef, rarityMult: number, rng: Rng, rarityShift = 0): Rarity {
  const weights = effectiveWeights(machine, rarityMult, rarityShift);
  return rng.weighted(RARITY_ORDER, (r) => weights[r]) ?? 'common';
}

/**
 * Contexte d'un tirage aux *Corrupted GachaDice* (A94-13) : les niveaux de
 * l'arbre décident des Matériaux tirables (*Matériologie*) et des dés Enrichis
 * (*Matériau Enrichi*).
 */
export interface PullContext {
  corrupted: true;
  levels: NodeLevels;
}

/**
 * Marque un dé tiré à une *Corrupted GachaDice* (A94-4, A94-5, A94-14) :
 * niveau **1** (A105 ; 1 à 10 avant la v0.53.2), Matériau à poids égaux parmi `availableMaterials`,
 * **Enrichi** à 10 % avec *Matériau Enrichi* (le `Rng` n'est alors tiré que
 * si le nœud est possédé), puis la marque **Corrupted** (v0.49.0, A98-6 :
 * forgeable, ×1000 dans `D_L`). Champs posés après `name`, dans cet ordre.
 */
export function markCorrupted(die: MasterDie, levels: NodeLevels, rng: Rng, level?: number): MasterDie {
  // v0.53.0 (A103) : un niveau imposé (départ en mode infini) remplace le tirage.
  die.level =
    level !== undefined && Number.isFinite(level) && level >= 1
      ? Math.floor(level)
      : rng.int(BALANCE.dieLevelGachaMin, BALANCE.dieLevelGachaMax);
  const materials = availableMaterials(levels);
  die.material = materials[rng.int(0, materials.length - 1)];
  die.enriched = nodeLevel(levels, 'aby_enriched') >= 1 && rng.chance(BALANCE.enrichedChance);
  die.corrupted = true;
  return die;
}

/**
 * Fabrique un dé Maître issu d'une machine. Un **Prismatique** est toujours un
 * dé nommé (A72-14), équiprobable parmi ceux que la machine permet (A123-1) :
 * Guinsoo, Chef de chantier, ArchiDicer, Andarielle, Croupier — et le Rat-Ailé
 * à une *Corrupted GachaDice* (contexte `corrupted`).
 *
 * v0.46.0 (A94) : avec un contexte `corrupted`, le dé reçoit en plus un
 * niveau, un Matériau et peut-être le caractère Enrichi (`markCorrupted`) —
 * un Prismatique reste un dé nommé. Les machines classiques ne donnent jamais
 * ces champs.
 */
export function pullDie(
  machine: MachineDef,
  rarityMult: number,
  rng: Rng,
  rarityShift = 0,
  ctx?: PullContext,
): MasterDie {
  const rarity = rollRarity(machine, rarityMult, rng, rarityShift);
  let die: MasterDie;
  // A123-1 : le Rat-Ailé n'est permis qu'aux Corrupted GachaDice.
  if (rarity === 'prismatic') die = createNamedPrismatic(machine.sides, rng, undefined, undefined, ctx?.corrupted === true);
  else {
    const affixes = rollAffixes(machine.sides, AFFIX_COUNT[rarity] ?? 0, rng);
    die = createMasterDie(machine.sides, rarity, affixes, rng);
  }
  return ctx?.corrupted ? markCorrupted(die, ctx.levels, rng) : die;
}

/**
 * Prix total d'un lot (linéaire, remise « Négociateur » incluse). `bal` porte
 * la surcharge `machineCostMult` (phase 6, 1 par défaut : prix inchangé).
 */
export function lotCost(
  machine: MachineDef,
  qty: number,
  stats: DerivedStats,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  return gachaUnitCost(machine.costPerDie * bal.machineCostMult, stats.gachaDiscountLevel) * qty;
}

/** La machine est-elle débloquée par l'arbre ? */
export function isMachineUnlocked(levels: NodeLevels, machine: MachineDef): boolean {
  if (!machine.requiresNode) return true;
  return nodeLevel(levels, machine.requiresNode) >= 1;
}

export interface PullOutcome {
  dice: MasterDie[];
  cost: number;
  /** Le meilleur dé du lot (score de dégâts attendus). */
  best: MasterDie | null;
}

/**
 * Ouvre un lot. Ne touche pas à l'or : l'appelant débite et range l'inventaire.
 *
 * *Gagnant-Gagnant* (`shop_twin`, COMMENTS v0.6.0 §1.1) : **chaque** dé tiré a
 * `stats.twinDieChance` (1 % par niveau) d'être accompagné d'un second dé de la
 * **même** machine, tiré indépendamment. Le lot peut donc rendre plus de dés
 * que demandé, sans coûter plus cher.
 */
export function pullLot(
  machine: MachineDef,
  qty: number,
  stats: DerivedStats,
  rng: Rng,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  ctx?: PullContext,
): PullOutcome {
  const dice: MasterDie[] = [];
  const twin = stats.twinDieChance ?? 0;
  const shift = bal.rarityShift;
  for (let i = 0; i < qty; i++) {
    dice.push(pullDie(machine, stats.rarityWeightMult, rng, shift, ctx));
    if (twin > 0 && rng.chance(twin)) dice.push(pullDie(machine, stats.rarityWeightMult, rng, shift, ctx));
  }
  return { dice, cost: lotCost(machine, qty, stats, bal), best: bestDie(dice, stats) };
}

/**
 * Dés triés par **rareté décroissante**, puis par nombre de faces décroissant —
 * COMMENTS v0.6.0 §2 : « les dés obtenus par une machine Gachapon sont
 * maintenant triés par ordre de rareté ». Tri **stable** : deux dés
 * indiscernables gardent leur ordre de tirage.
 */
export function sortByRarity(dice: readonly MasterDie[]): MasterDie[] {
  return dice.slice().sort((a, b) => {
    const rarity = RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
    if (rarity !== 0) return rarity;
    return b.sides - a.sides;
  });
}

/** Le meilleur dé d'une liste (score de dégâts attendus, rareté en départage). */
export function bestDie(dice: readonly MasterDie[], stats?: DerivedStats): MasterDie | null {
  let best: MasterDie | null = null;
  let bestValue = -Infinity;
  for (const die of dice) {
    const value = dieScore(die, stats) + RARITY_ORDER.indexOf(die.rarity) * 0.001;
    if (value > bestValue) {
      bestValue = value;
      best = die;
    }
  }
  return best;
}

/**
 * Valeur de revente d'un dé : pourcentage du prix de sa machine d'origine.
 * Le prix de référence est le prix catalogue (sans la remise Négociateur),
 * pour éviter qu'acheter moins cher ne dévalue la revente.
 */
export function recycleValue(die: MasterDie): number {
  const machine = machineForSides(die.sides);
  return Math.max(1, Math.round(machine.costPerDie * (RECYCLE_PERCENT[die.rarity] ?? 0)));
}

export function machineById(id: MachineId): MachineDef {
  return MACHINE_BY_ID[id];
}
